import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);
const IDLE_LIMIT_MS = 3 * 60 * 60 * 1000;
const ACTIVITY_KEY = 'evalon-last-activity';

function getCookie(name) {
  let cookieValue = null;
  if (document.cookie && document.cookie !== '') {
    const cookies = document.cookie.split(';');
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].trim();
      if (cookie.substring(0, name.length + 1) === (name + '=')) {
        cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
        break;
      }
    }
  }
  return cookieValue;
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const login = async (username, password) => {
    const csrftoken = getCookie('csrftoken');
    await axios.post('/api/login/', { username, password }, {
      headers: { 'X-CSRFToken': csrftoken }
    });
    const { data } = await axios.get('/api/user/');
    localStorage.setItem(ACTIVITY_KEY, String(Date.now()));
    setUser(data);
  };

  const logout = async () => {
    const csrftoken = getCookie('csrftoken');
    try {
      await axios.post('/api/logout/', null, {
        headers: { 'X-CSRFToken': csrftoken }
      });
    } finally {
      setUser(null);
      localStorage.removeItem(ACTIVITY_KEY);
    }
  };

  // --- NOVA FUNÇÃO ADICIONADA ---
  const clientSideLogout = () => {
    setUser(null);
    localStorage.removeItem(ACTIVITY_KEY);
  };

  useEffect(() => {
    if (!user) return undefined;

    let lastHeartbeat = Date.now();
    let checkingSession = false;
    let expiring = false;
    const expire = () => {
      if (expiring) return;
      expiring = true;
      localStorage.removeItem(ACTIVITY_KEY);
      setUser(null);
      axios.post('/api/logout/', null, {
        headers: { 'X-CSRFToken': getCookie('csrftoken') },
        timeout: 3000
      }).catch(() => {}).finally(() => window.location.replace('/login'));
    };
    const checkIdle = () => {
      const lastActivity = Number(localStorage.getItem(ACTIVITY_KEY));
      if (lastActivity && Date.now() - lastActivity >= IDLE_LIMIT_MS) expire();
    };
    const onActivity = () => {
      const now = Date.now();
      const lastActivity = Number(localStorage.getItem(ACTIVITY_KEY));
      if (lastActivity && now - lastActivity >= IDLE_LIMIT_MS) {
        expire();
        return;
      }
      localStorage.setItem(ACTIVITY_KEY, String(now));
      // Renova a sessão no servidor somente quando houver uso real da página.
      if (!checkingSession && now - lastHeartbeat >= 5 * 60 * 1000) {
        checkingSession = true;
        lastHeartbeat = now;
        axios.get('/api/user/').catch(error => {
          if (error.response?.status === 401 || error.response?.status === 403) expire();
        }).finally(() => { checkingSession = false; });
      }
    };

    checkIdle();
    const events = ['pointerdown', 'keydown', 'scroll', 'touchstart'];
    events.forEach(event => window.addEventListener(event, onActivity, { passive: true }));
    const interval = window.setInterval(checkIdle, 30000);
    window.addEventListener('focus', checkIdle);
    return () => {
      events.forEach(event => window.removeEventListener(event, onActivity));
      window.clearInterval(interval);
      window.removeEventListener('focus', checkIdle);
    };
  }, [user]);

  useEffect(() => {
    const checkUser = async () => {
      try {
        const { data } = await axios.get('/api/user/');
        if (!localStorage.getItem(ACTIVITY_KEY)) {
          localStorage.setItem(ACTIVITY_KEY, String(Date.now()));
        }
        setUser(data);
      } catch (error) {
        setUser(null);
        localStorage.removeItem(ACTIVITY_KEY);
      } finally {
        setLoading(false);
      }
    };
    checkUser();
  }, []);

  // --- Adicionamos a nova função ao valor compartilhado ---
  const value = { user, loading, login, logout, clientSideLogout };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  return useContext(AuthContext);
};
